/**
 * Scene transport — play/pause, scrub, speed.
 *
 * Scrubbing works because every actor pose is a pure function of the clock:
 * seeking anywhere re-derives the whole scene, forwards or backwards.
 */
import { Pause, Play, Repeat, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useStudioStore, selectActiveScene } from '../store/studioStore'
import { PANEL } from './studioStyles'

const SPEEDS = [0.25, 0.5, 1, 2, 4]

const fmt = (ms: number) => {
  const total = Math.max(0, Math.round(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function StudioTimelineBar({ durationMs }: { durationMs: number }) {
  const playback = useStudioStore((s) => s.playback)
  const currentMs = useStudioStore((s) => s.currentMs)
  const speed = useStudioStore((s) => s.speed)
  const play = useStudioStore((s) => s.play)
  const pause = useStudioStore((s) => s.pause)
  const seek = useStudioStore((s) => s.seek)
  const setSpeed = useStudioStore((s) => s.setSpeed)
  const scene = useStudioStore(selectActiveScene)
  const updateSettings = useStudioStore((s) => s.updateSettings)

  const isPlaying = playback === 'playing'
  // Scene loop lives here as well as in the Scenes panel: it defaults to ON, and
  // "why won't playback stop?" is a question you ask at the transport controls,
  // not three panels away.
  const loopScene = scene?.settings.loopScene ?? true

  return (
    <div
      className={cn(
        PANEL,
        'absolute bottom-6 left-1/2 w-[560px] max-w-[calc(100vw-2rem)] -translate-x-1/2 px-4 py-3'
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => (isPlaying ? pause() : play())}
          className="flex h-8 w-8 items-center justify-center rounded-md bg-sky-500/30 text-sky-100 hover:bg-sky-500/40"
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={() => {
            seek(0)
            play()
          }}
          className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10 text-white/70 hover:bg-white/20"
          title="Replay from the start"
        >
          <RotateCcw className="h-4 w-4" />
        </button>

        <span className="ml-1 text-sm font-semibold tabular-nums text-white">
          {fmt(currentMs)}
          <span className="text-white/35"> / {fmt(durationMs)}</span>
        </span>

        <button
          type="button"
          onClick={() => updateSettings({ loopScene: !loopScene })}
          disabled={!scene}
          title={
            loopScene
              ? 'Looping — playback restarts at the end. Click to play once and stop.'
              : 'Play once — playback stops at the end. Click to loop.'
          }
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md disabled:opacity-30',
            loopScene
              ? 'bg-sky-500/30 text-sky-200 hover:bg-sky-500/40'
              : 'bg-white/10 text-white/50 hover:bg-white/20'
          )}
        >
          <Repeat className="h-4 w-4" />
        </button>

        <div className="flex-1" />

        <div className="flex overflow-hidden rounded-md border border-white/10">
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSpeed(s)}
              className={cn(
                'px-1.5 py-1 text-[11px] font-medium tabular-nums',
                Math.abs(speed - s) < 0.01
                  ? 'bg-sky-500/30 text-sky-200'
                  : 'text-white/50 hover:bg-white/5'
              )}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>

      <input
        type="range"
        min={0}
        max={Math.max(1, durationMs)}
        step={50}
        value={Math.min(currentMs, durationMs)}
        onChange={(e) => seek(Number(e.target.value))}
        className="h-1 w-full cursor-pointer accent-sky-400"
      />
    </div>
  )
}
